import { apiClient } from '../api';
import type {
    RegisterPushDevicePayload,
    RegisterPushDeviceResponse,
} from '../../types/push';

const TRANSIENT_ERROR_CODES = new Set(['FETCH_ERROR', 'TIMEOUT', 'NETWORK_ERROR']);
const RETRY_DELAYS_MS = [500, 1500, 3000];

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function isRetryableError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
        return true;
    }

    const apiError = error as {
        message?: string;
        error_code?: string;
        errors?: Record<string, string[]>;
    };

    if (apiError.errors) {
        return false;
    }

    if (apiError.message?.toLowerCase().includes('unauthenticated')) {
        return false;
    }

    if (apiError.error_code && !TRANSIENT_ERROR_CODES.has(apiError.error_code)) {
        return false;
    }

    return true;
}

/**
 * Registrar token push con reintentos para fallos transitorios.
 */
export async function registerPushDevice(
    payload: RegisterPushDevicePayload
): Promise<RegisterPushDeviceResponse> {
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
        try {
            return await apiClient.post<RegisterPushDeviceResponse>(
                '/api/push/devices/register',
                payload
            );
        } catch (error) {
            lastError = error;

            if (!isRetryableError(error) || attempt === RETRY_DELAYS_MS.length) {
                throw error;
            }

            await sleep(RETRY_DELAYS_MS[attempt]);
        }
    }

    throw lastError ?? new Error('Error desconocido al registrar dispositivo push.');
}
